import React, { useContext, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ThemeContext } from '../../hooks/ThemeContext';
import { CartContext } from '../../hooks/CartContext';
import { useProductById } from '../../hooks/useProducts';
import ProtectedImage from '../../components/common/ProtectedImage';
import LoadingSpinner from '../../components/globals/LoadingSpinner';
import SectionTitle from '../../components/common/SectionTitle';
import NotFound from '../../components/globals/NotFound';
import { UserContext } from '../../hooks/UserContext';
import { api } from '../../services/baseUrl';

import toast from 'react-hot-toast';
import { formatCurrency, formatPercent } from '../../utils/formatters';
import { ArrowLeft, Lock, ShieldCheck, ShoppingBag, Star, BadgeEuro, Layers3, Brush } from 'lucide-react';

const ProductDetails = () => {
  const { theme } = useContext(ThemeContext);
  const { user } = useContext(UserContext);
  const { addToCart } = useContext(CartContext);
  const { id } = useParams();
  const navigate = useNavigate();
  const isDark = theme === 'dark';

  const { product, allRatings, loading, error, notFound, forbidden, refetch } = useProductById(id);
  const [ratingAverage, setRatingAverage] = useState(0);
  const [ratingCount, setRatingCount] = useState(0);
  const [userRating, setUserRating] = useState(null);
  const [userComment, setUserComment] = useState('');
  const [canRate, setCanRate] = useState(false);
  const [ratingSending, setRatingSending] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  useEffect(() => {
    if (!product) return;
    setRatingAverage(product.rating_average ?? 0);
    setRatingCount(product.rating_count ?? 0);
    setUserRating(product.user_rating ?? null);
    setUserComment(product.user_comment ?? '');
    setCanRate(Boolean(product.can_rate));
    setSelectedImageIndex(0);
  }, [product]);

  const handleAddToCart = async () => {
    const productId = product?.id_product ?? product?.id;
    if (!productId || !canPurchase) {
      toast.error("Ce produit n'est pas disponible pour votre compte.");
      return;
    }

    await addToCart(productId, 1);
    // Actualiser le produit pour récupérer le stock mis à jour
    if (refetch) {
      await refetch();
    }
  };

  if (loading) {
    return (
      <div className={`flex-1 flex flex-col ${isDark ? 'bg-[#0f1114] text-white' : 'bg-slate-50 text-gray-800'}`}>
        <div className="flex justify-center items-center py-20">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (notFound) {
    return <NotFound />;
  }

  if (forbidden) {
    return (
      <div className={`flex-1 flex flex-col ${isDark ? 'bg-[#0f1114] text-white' : 'bg-slate-50 text-gray-800'}`}>
        <div className="container mx-auto px-4 py-16 max-w-4xl">
          <div className={`rounded-[32px] border p-8 md:p-10 shadow-xl ${isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-white'}`}>
            <div className="mb-5 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-rose-500/15 text-rose-400">
              <Lock className="h-7 w-7" />
            </div>
            <h1 className="text-3xl font-bold">Produit reserve</h1>
            <p className={`mt-4 max-w-2xl text-base leading-8 ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
              Cette creation n'est pas accessible depuis votre compte. Son prix et son ajout au panier sont volontairement masques.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button
                onClick={() => navigate('/creations')}
                className="inline-flex items-center gap-2 rounded-2xl bg-rose-600 px-5 py-3 font-semibold text-white transition hover:bg-rose-700"
              >
                <ArrowLeft className="h-4 w-4" />
                Retour aux creations
              </button>
              <button
                onClick={() => navigate('/shop')}
                className={`rounded-2xl border px-5 py-3 font-semibold transition ${isDark ? 'border-white/10 bg-white/5 hover:bg-white/10' : 'border-gray-200 bg-white hover:bg-gray-50'}`}
              >
                Voir la boutique
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className={`flex-1 flex flex-col ${isDark ? 'bg-[#0f1114] text-white' : 'bg-slate-50 text-gray-800'}`}>
        <div className="container mx-auto px-4 py-12 max-w-5xl">
          <div className={`p-4 rounded-lg ${isDark ? 'bg-red-500/20 text-red-300 border border-red-500/30' : 'bg-red-50 text-red-800 border border-red-200'}`}>
            {error || 'Produit introuvable'}
          </div>
          <button
            onClick={() => navigate('/shop')}
            className="mt-6 px-4 py-2 rounded-lg bg-rose-600 text-white hover:bg-rose-700 transition-colors"
          >
            Retour à la boutique
          </button>
        </div>
      </div>
    );
  }

  const title = product.name_product || product.name || 'Produit';
  const description = product.description_product || product.description || '';
  const price = product.price_product || product.price || 0;
  const promoPrice = product.price_promo ?? product.price_promo_ttc ?? null;
  const promoActive = Boolean(product.promo_active);
  const promoDiscount = product.promo_discount;
  const rawStock = product.available_stock ?? product.stock ?? product.quantity_product ?? 0;
  const stock = rawStock === 0 ? 'Illimité' : rawStock;
  const saleType = product.sale_type || 'libre';
  const canPurchase = Boolean(product.can_purchase ?? true);
  const canViewPrice = Boolean(product.can_view_price ?? canPurchase);
  const category = product.category || product.category_name || 'Collection';
  const creatorName = product.creator_name || product.creator?.nickname || 'Krayne Agency';
  const galleryImages = Array.isArray(product.images) && product.images.length > 0
    ? product.images
    : [product.image ?? product.image_url ?? product.path_watermarked ?? product.path_image].filter(Boolean);
  const activeImage = galleryImages[selectedImageIndex] || galleryImages[0] || product.image;
  const priceLabel = promoActive && promoPrice !== null ? formatCurrency(promoPrice) : formatCurrency(price);


  return (
    <div className={`flex-1 flex flex-col ${isDark ? 'bg-[#0f1114] text-white' : 'bg-slate-50 text-gray-800'}`}>
      <div className={`${isDark ? 'bg-[radial-gradient(circle_at_top_left,_rgba(244,63,94,0.22),_transparent_32%),radial-gradient(circle_at_85%_15%,_rgba(59,130,246,0.16),_transparent_25%),linear-gradient(180deg,#0b0d12,#12161f)] border-b border-white/10' : 'bg-[radial-gradient(circle_at_top_left,_rgba(251,113,133,0.22),_transparent_30%),radial-gradient(circle_at_85%_15%,_rgba(96,165,250,0.16),_transparent_25%),linear-gradient(180deg,#fff7f5,#f7fafc)] border-b border-gray-200'}`}>
        <div className="container mx-auto px-4 py-12 max-w-6xl">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
            <button
              onClick={() => navigate('/shop')}
              className={`inline-flex items-center gap-2 px-5 py-3 rounded-2xl border shadow-xl transition-colors ${isDark ? 'border-white/10 bg-white/5 text-white hover:bg-white/10' : 'border-gray-200 bg-white text-gray-800 hover:bg-gray-50'}`}
            >
              <ArrowLeft className="w-4 h-4" />
              Retour boutique
            </button>
            <div className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm shadow-xl ${isDark ? 'border-white/10 bg-white/5 text-gray-200' : 'border-gray-200 bg-white text-gray-700'}`}>
              <ShieldCheck className="w-4 h-4 text-rose-400" />
              Détail produit premium
            </div>
          </div>

          <div className="grid gap-8 lg:grid-cols-[1.15fr_0.85fr] items-start">
            <div className="space-y-6">
              <div className="flex flex-wrap gap-3">
                <span className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] ${isDark ? 'bg-white/5 text-white/80 border border-white/10' : 'bg-white text-gray-700 border border-gray-200'}`}>
                  <Layers3 className="h-4 w-4 text-rose-400" />
                  {category}
                </span>
                <span className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] ${isDark ? 'bg-white/5 text-white/80 border border-white/10' : 'bg-white text-gray-700 border border-gray-200'}`}>
                  <Brush className="h-4 w-4 text-sky-400" />
                  {creatorName}
                </span>
              </div>

              <SectionTitle
                title={title}
                subtitle={saleType === 'unique' ? 'Piece reservee a un acheteur cible' : `Stock disponible : ${stock}`}
                align="left"
              />

              <p className={`max-w-3xl text-base leading-8 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                {description || 'Une creation pensee pour etre consultee comme une piece de collection, avec une lecture plus claire des visuels, du contexte et des conditions d achat.'}
              </p>

              <div className="grid gap-4 sm:grid-cols-3">
                {[
                { label: 'Prix', value: canViewPrice ? priceLabel : 'Reserve', icon: <BadgeEuro className="w-5 h-5 text-rose-400" /> },
                { label: 'Avis', value: `${ratingAverage ? ratingAverage.toFixed(1) : '0.0'} / 5`, icon: <Star className="w-5 h-5 text-rose-400" /> },
                { label: 'Acces', value: saleType === 'unique' ? 'Unique' : 'Ouvert', icon: <ShieldCheck className="w-5 h-5 text-rose-400" /> },
              ].map((item) => (
                <div key={item.label} className={`rounded-3xl border p-5 shadow-xl ${isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-white'}`}>
                  <div className="mb-3 inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-rose-900/20 via-purple-900/20 to-blue-900/20 shadow-xl">
                    {item.icon}
                  </div>
                  <p className="text-xl font-bold mb-1">{item.value}</p>
                  <p className={`${isDark ? 'text-sm text-gray-300' : 'text-sm text-gray-600'}`}>{item.label}</p>
                </div>
              ))}
              </div>

            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12 max-w-6xl">

        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.05fr_0.95fr]">
          <div className="space-y-6">
            <div className={`rounded-[32px] overflow-hidden shadow-xl ${isDark ? 'bg-white/5 border border-white/10' : 'bg-white border border-gray-200'}`}>
            <div className="aspect-video">
              <ProtectedImage
                imagePath={activeImage}
                alt={title}
                className="w-full h-full"
                objectFit="contain"
                showWatermark={true}
              />
            </div>
            {galleryImages.length > 1 && (
              <div className={`border-t p-4 ${isDark ? 'border-white/10 bg-gradient-to-br from-rose-900/20 via-purple-900/20 to-blue-900/20' : 'border-gray-200 bg-gray-50/80'}`}>
                <div className="grid grid-cols-4 gap-3 sm:grid-cols-5">
                  {galleryImages.slice(0, 8).map((image, index) => (
                    <button
                      key={`${image}-${index}`}
                      type="button"
                      onClick={() => setSelectedImageIndex(index)}
                      className={`overflow-hidden rounded-2xl border transition shadow-xl ${selectedImageIndex === index
                        ? 'border-rose-500 ring-2 ring-rose-500/30'
                        : isDark ? 'border-white/10 hover:border-white/30' : 'border-gray-200 hover:border-gray-300'}`}
                    >
                      <ProtectedImage
                        imagePath={image}
                        alt={`${title} miniature ${index + 1}`}
                        className="h-20 w-full"
                        objectFit="cover"
                        showWatermark={true}
                      />
                    </button>
                  ))}
                </div>
              </div>
            )}
            </div>

          </div>

          <div className={`rounded-[32px] p-6 md:p-8 shadow-xl lg:sticky lg:top-24 ${isDark ? 'bg-white/5 border border-white/10' : 'bg-white border border-gray-200'}`}>
            <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold">{title}</h2>
                <p className={`mt-2 text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                  {saleType === 'unique' ? 'Edition ciblee ou reservee' : 'Produit disponible a la commande'}
                </p>
              </div>
              <span className={`inline-flex items-center rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] ${saleType === 'unique' ? (isDark ? 'bg-amber-500/15 text-amber-300 border border-amber-500/20' : 'bg-amber-50 text-amber-700 border border-amber-200') : (isDark ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/20' : 'bg-emerald-50 text-emerald-700 border border-emerald-200')}`}>
                {saleType === 'unique' ? 'Unique' : 'Libre'}
              </span>
            </div>

            <div className={`mb-6 rounded-[28px] border p-5 md:p-6 ${isDark ? 'border-white/10 bg-[linear-gradient(135deg,rgba(244,63,94,0.14),rgba(59,130,246,0.12))]' : 'border-gray-200 bg-[linear-gradient(135deg,rgba(255,241,242,0.95),rgba(239,246,255,0.95))]'}`}>
              <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex flex-col">
                {canViewPrice && promoActive && promoPrice !== null ? (
                  <div className="flex items-baseline gap-3">
                    <span className={`text-3xl font-bold ${isDark ? 'text-rose-400' : 'text-rose-600'}`}>
                      {formatCurrency(promoPrice)}
                    </span>
                    <span className={`text-sm line-through ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                      {formatCurrency(price)}
                    </span>
                  </div>
                ) : canViewPrice ? (
                  <span className={`text-3xl font-bold ${isDark ? 'text-rose-400' : 'text-rose-600'}`}>
                    {formatCurrency(price)}
                  </span>
                ) : (
                  <span className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Tarif masque
                  </span>
                )}
                {canViewPrice && promoActive && promoDiscount ? (
                  <span className={`text-xs font-semibold ${isDark ? 'text-emerald-300' : 'text-emerald-600'}`}>
                    -{formatPercent(promoDiscount, 0)}
                  </span>
                ) : (
                  <span className={`text-xs font-medium ${isDark ? 'text-white/55' : 'text-gray-500'}`}>
                    Visible uniquement aux comptes autorises.
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      disabled={!canRate || ratingSending}
                      onClick={async () => {
                        if (!canRate) return;
                        const productId = product?.id_product ?? product?.id;
                        if (!productId) return;
                        try {
                          setRatingSending(true);
                          const response = await api.post(`/products/${productId}/rating`, {
                            rating: star,
                            comment: userComment || null,
                          });
                          setUserRating(star);
                          setRatingAverage(response.data?.rating_average ?? star);
                          setRatingCount(response.data?.rating_count ?? ratingCount);
                          setUserComment(response.data?.user_comment ?? userComment);
                          if (refetch) await refetch();
                        } catch (err) {
                          console.error('Erreur vote:', err);
                        } finally {
                          setRatingSending(false);
                        }
                      }}
                      className={`text-xl transition ${
                        (userRating || Math.round(ratingAverage)) >= star
                          ? 'text-yellow-400'
                          : isDark ? 'text-white/30' : 'text-gray-300'
                      } ${canRate ? 'hover:scale-110' : 'cursor-default'}`}
                      aria-label={`Noter ${star} etoile${star > 1 ? 's' : ''}`}
                    >
                      ★
                    </button>
                  ))}
                </div>
                <span className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                  {ratingAverage ? ratingAverage.toFixed(1) : '0.0'} ({ratingCount})
                </span>
              </div>
              <span className={`text-sm ${isDark ? 'text-white/60' : 'text-gray-500'}`}>
                {saleType === 'unique' ? 'Exemplaire unique' : `Stock: ${stock}`}
              </span>
              </div>
              {!canPurchase && (
                <div className={`mt-4 rounded-2xl border px-4 py-3 text-sm ${isDark ? 'border-amber-500/20 bg-amber-500/10 text-amber-200' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
                  Ce produit n'est pas achetable depuis votre compte. L'ajout au panier et le tarif sont verrouilles.
                </div>
              )}
            </div>

            {/* Liste des avis acheteurs */}
            {allRatings && allRatings.length > 0 && (
              <div className="mb-6">
                <h3 className={`text-base font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`}>Avis des acheteurs</h3>
                <div className="space-y-3">
                  {allRatings.map((r, idx) => (
                    <div key={idx} className={`rounded-2xl p-4 border shadow-xl ${isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-gray-50'}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold">{r.user}</span>
                        <span className="text-yellow-400">{'★'.repeat(r.rating)}</span>
                        <span className="text-xs ml-2 opacity-60">{r.created_at}</span>
                      </div>
                      <div className={`text-sm ${isDark ? 'text-white/80' : 'text-gray-700'}`}>{r.comment}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!user && (
              <div className={`mb-4 text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                Connectez-vous pour noter ce produit.
              </div>
            )}
            {user && !canRate && (
              <div className={`mb-4 text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                Achat requis pour laisser une note.
              </div>
            )}

            {user && canRate && (
              <div className="mb-4">
                <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-white/70' : 'text-gray-700'}`}>
                  {product.user_rating ? 'Modifier votre commentaire' : 'Votre commentaire'}
                </label>
                <textarea
                  rows={3}
                  value={userComment}
                  onChange={(e) => setUserComment(e.target.value)}
                  placeholder="Partagez un mot sur ce produit..."
                  className={`w-full rounded-2xl border px-4 py-3 text-sm outline-none transition ${
                    isDark
                      ? 'bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-rose-500'
                      : 'bg-white border-gray-200 text-gray-800 placeholder:text-gray-400 focus:border-rose-500'
                  }`}
                />
                <button
                  type="button"
                  disabled={!userRating || ratingSending}
                  onClick={async () => {
                    if (!userRating) return;
                    const productId = product?.id_product ?? product?.id;
                    if (!productId) return;
                    try {
                      setRatingSending(true);
                      const response = await api.post(`/products/${productId}/rating`, {
                        rating: userRating,
                        comment: userComment || null,
                      });
                      setRatingAverage(response.data?.rating_average ?? userRating);
                      setRatingCount(response.data?.rating_count ?? ratingCount);
                      setUserComment(response.data?.user_comment ?? userComment);
                      toast.success('Votre avis a été enregistré !');
                    } catch (err) {
                      toast.error("Erreur lors de l'enregistrement de l'avis");
                      console.error('Erreur commentaire:', err);
                    } finally {
                      setRatingSending(false);
                    }
                  }}
                  className={`mt-3 px-4 py-2 rounded-lg text-sm font-semibold transition ${
                    !userRating || ratingSending
                      ? (isDark ? 'bg-white/10 text-white/40' : 'bg-gray-100 text-gray-400')
                      : (isDark ? 'bg-rose-500/20 text-rose-300 hover:bg-rose-500/30 shadow-xl rounded-2xl' : 'bg-rose-50 text-rose-700 hover:bg-rose-100 shadow-xl rounded-2xl')
                  }`}
                >
                  {product.user_rating ? 'Mettre à jour' : 'Enregistrer'} le commentaire
                </button>
                {product.user_rating && (
                  <div className={`mt-2 text-xs ${isDark ? 'text-emerald-300' : 'text-emerald-700'}`}>
                    Votre note actuelle : <span className="font-bold">{product.user_rating} ★</span>
                    {product.user_comment && (
                      <span> — "{product.user_comment}"</span>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-3 flex-wrap">
              <button
                onClick={handleAddToCart}
                disabled={!canPurchase}
                className={`px-5 py-3 rounded-2xl font-semibold transition-colors shadow-xl ${canPurchase ? 'bg-rose-600 text-white hover:bg-rose-700' : (isDark ? 'bg-white/10 text-white/35 cursor-not-allowed' : 'bg-gray-100 text-gray-400 cursor-not-allowed')}`}
              >
                <span className="inline-flex items-center gap-2">
                  <ShoppingBag className="h-4 w-4" />
                  {canPurchase ? 'Ajouter au panier' : 'Achat indisponible'}
                </span>
              </button>
              <button
                onClick={() => navigate('/shop')}
                className={`px-5 py-3 rounded-2xl border font-medium transition-colors shadow-xl ${
                  isDark
                    ? 'border-white/20 text-white bg-white/5 hover:bg-white/10'
                    : 'border-gray-300 text-gray-800 hover:bg-gray-50'
                }`}
              >
                Retour boutique
              </button>
            </div>

            <div className={`mt-6 text-xs rounded-2xl border p-4 ${isDark ? 'text-white/60 border-white/10 bg-white/5' : 'text-gray-500 border-gray-200 bg-slate-50'}`}>
              🔒 Image protégée par filigrane et lien expirable.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;
